import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Building } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';

const EMPTY = {
  company_name: '', gstin: '', registered_address: '',
  primary_contact_name: '', primary_contact_mobile: '', primary_contact_email: '',
  contract_start_date: '', contract_end_date: '',
  discount_pct: 10, billing_terms: 'Net 30', status: 'active', notes: '',
};

export default function NewCorporateAccount() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { create: apiCreate, update: apiUpdate, fetch: apiFetch } = useApiMethods({ throwOnFail: true });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`engage/corporate-accounts/${id}/`);
        const d = res?.data || res?.data?.data;
        if (d) setForm({ ...EMPTY, ...d,
          contract_start_date: d.contract_start_date || '',
          contract_end_date: d.contract_end_date || '' });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load account');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const payload = { ...form };
      if (!payload.contract_start_date) delete payload.contract_start_date;
      if (!payload.contract_end_date) delete payload.contract_end_date;
      if (isEdit) {
        await apiUpdate(`/engage/corporate-accounts/${id}/`, payload);
        navigate(`/engage/corporate-accounts/${id}`);
      } else {
        const resp = await apiCreate('/engage/corporate-accounts/', payload);
        const created = resp?.data || resp?.data?.data;
        navigate(created?.id ? `/engage/corporate-accounts/${created.id}` : '/engage/corporate-accounts');
      }
    } catch (err) {
      setError(err?.response?.data?.message || (err?.response?.data && JSON.stringify(err.response.data)) || 'Failed to save');
      setSaving(false);
    }
  };

  const backTo = isEdit ? `/engage/corporate-accounts/${id}` : '/engage/corporate-accounts';
  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-sm"><Building className="w-5 h-5" /></div>
        <div><h1 className="text-xl font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit' : 'Add'} Corporate Account</h1></div>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><L>Company name <span className="text-red-500">*</span></L><input required value={form.company_name} onChange={e => set('company_name', e.target.value)} className={INPUT} /></div>
          <div><L>GSTIN</L><input value={form.gstin} onChange={e => set('gstin', e.target.value)} className={INPUT} /></div>
          <div><L>Primary contact name</L><input value={form.primary_contact_name} onChange={e => set('primary_contact_name', e.target.value)} className={INPUT} /></div>
          <div><L>Contact mobile</L><input value={form.primary_contact_mobile} onChange={e => set('primary_contact_mobile', e.target.value)} className={INPUT} /></div>
          <div><L>Contact email</L><input type="email" value={form.primary_contact_email} onChange={e => set('primary_contact_email', e.target.value)} className={INPUT} /></div>
          <div><L>Discount %</L><input type="number" value={form.discount_pct} onChange={e => set('discount_pct', e.target.value)} className={INPUT} /></div>
          <div><L>Contract start</L><input type="date" value={form.contract_start_date || ''} onChange={e => set('contract_start_date', e.target.value)} className={INPUT} /></div>
          <div><L>Contract end</L><input type="date" value={form.contract_end_date || ''} onChange={e => set('contract_end_date', e.target.value)} className={INPUT} /></div>
          <div><L>Billing terms</L><input value={form.billing_terms} onChange={e => set('billing_terms', e.target.value)} className={INPUT} placeholder="Net 30" /></div>
          <div>
            <L>Status</L>
            <select value={form.status} onChange={e => set('status', e.target.value)} className={INPUT}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="expired">Expired</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div><L>Registered address</L><textarea rows={2} value={form.registered_address} onChange={e => set('registered_address', e.target.value)} className={INPUT} /></div>
        <div><L>Notes</L><textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} className={INPUT} /></div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate(backTo)} className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button type="submit" disabled={saving || !form.company_name.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Account')}
          </button>
        </div>
      </form>
    </div>
  );
}
